import { Outlet, useMatches, useNavigate } from "@tanstack/react-router";
import "../../assets/scss/style.scss";
import { Sidebar } from "../layout/sidebar/Sidebar";
import {
  useEffect,
  useState,
  useDeferredValue,
  useRef,
  useCallback,
  startTransition,
  Suspense,
} from "react";
import { useTranslation } from "react-i18next";
import Navbar from "../layout/navbar/Navbar";
import { clearStore, useStore } from "../../store/store";
import { useBasicFlags } from "@/hooks/useBasicFlags";
import GlobalDataVisualizationModal from "@/components/common/generic-data-visualization-modal/GlobalDataVisualizationModal";
import { useGetUserDetailsQuery } from "../../api/common/common.hooks";
import { commonService } from "../../api/common/common.service";
import {
  resolveAuthRedirect,
  resolveLandingPage,
  ROUTES,
} from "@/utils/routingUtils";
import { useBranding } from "../../hooks/useBranding";
import { useToast } from "@/components/ui";
import PageLoader from "../layout/loaders/PageLoader";

const AppLayout = () => {
  const { i18n } = useTranslation();
  const [dir, setDir] = useState(i18n.dir());
  const isSidebarToggle = useStore.use.isSidebarToggle();
  const setSidebarToggle = useStore.use.setSidebarToggle();
  const defferedIsSidebarToggle = useDeferredValue(isSidebarToggle);
  const { featureShowHideFlag } = useBasicFlags();

  const matches = useMatches();
  const navigate = useNavigate();
  const { fetchBranding } = useBranding();
  const { toast } = useToast();

  const isUpdatePasswordRoute = matches.some(
    (m) => m.pathname === "/auth/update-password",
  );
  const isRoleSelectRoute = matches.some(
    (m) => m.pathname === "/auth/role-select",
  );

  const setGlobalDateFormat = useStore.use.setGlobalDateFormat();
  const updateAuthStore = useStore.use.updateAuthStore();
  const setEntityFeatureFlagShowHide =
    useStore.use.setEntityFeatureFlagShowHide();

  const [isFeatureFlagLoading, setIsFeatureFlagLoading] = useState(true);
  const [roleResolved, setRoleResolved] = useState(false);
  const brandingFetchedRef = useRef(false);
  const bootstrapErrorHandledRef = useRef(false);

  const shouldFetchUserDetails = !isRoleSelectRoute;

  const getUserDetailData = useGetUserDetailsQuery(
    {},
    { enabled: shouldFetchUserDetails },
  );

  // Don't render app content until we've run user-details + resolveAuthRedirect and know we're not
  // redirecting to role-select or update-password (avoids flashing the wrong page before redirect).
  const needsRoleResolution = !isUpdatePasswordRoute && !isRoleSelectRoute;
  const roleResolutionPending = needsRoleResolution && !roleResolved;

  /**
   * Fetches entity feature show/hide flags (which modules/features are enabled for the entity)
   * and stores them via setEntityFeatureFlagShowHide. Used when the store has no flags yet.
   */
  const fetchEntityFeatureFlag = useCallback(
    async (entityId: string) => {
      if (!entityId) return;
      try {
        const res = await commonService.getEntityFeatureShowHideFlag(entityId);
        setEntityFeatureFlagShowHide(res?.data);
      } catch (err: unknown) {
        toast({
          variant: "destructive",
          title: `Error fetching entity feature- ${err instanceof Error ? err.message : String(err)}.`,
        });
      } finally {
        setIsFeatureFlagLoading(false);
      }
    },
    [setEntityFeatureFlagShowHide, toast],
  );

  /**
   * After user-details + auth redirect: loads branding and, if the store has no feature flags yet,
   * fetches them via fetchEntityFeatureFlag. Supports onDone for multi-role flow (load then redirect to role-select).
   */
  const loadBrandingAndFeatureFlags = useCallback(
    async (entityId: string | undefined, onDone?: () => void) => {
      setRoleResolved(true);
      brandingFetchedRef.current = true;
      await fetchBranding(entityId);
      const promises: Promise<unknown>[] = [];
      if (
        !Array.isArray(featureShowHideFlag) ||
        featureShowHideFlag.length === 0
      ) {
        setIsFeatureFlagLoading(true);
        if (entityId) promises.push(fetchEntityFeatureFlag(entityId));
      } else if (!onDone) {
        // Only hide loader when not redirecting (multi-role keeps loader until onDone)
        setIsFeatureFlagLoading(false);
      }
      void Promise.all(promises).finally(() => {
        if (onDone) {
          onDone();
          // Keep loader visible until we navigate away; don't set loading false
        } else {
          setIsFeatureFlagLoading(false);
        }
      });
    },
    [fetchBranding, fetchEntityFeatureFlag, featureShowHideFlag],
  );

  // User-detail success: update store, resolve auth redirect, fetch branding + entity-feature
  useEffect(() => {
    if (isUpdatePasswordRoute || isRoleSelectRoute) return;
    const ready =
      !getUserDetailData?.isFetching &&
      !getUserDetailData?.isLoading &&
      getUserDetailData?.isSuccess &&
      getUserDetailData.data?.data &&
      !brandingFetchedRef.current;

    if (!ready) return;

    const userData = getUserDetailData.data?.data;

    updateAuthStore({ user: userData });

    const redirectTo = resolveAuthRedirect(userData);

    if (!userData.entity?.internal_id) {
      toast({
        title: "No entity provided",
        variant: "destructive",
      });
      setIsFeatureFlagLoading(false);
      useStore.getState().resetAuthStore();
      clearStore();
      navigate({ to: "/auth/login", replace: true });
    }
    setGlobalDateFormat(userData?.country_locale ?? "");

    void loadBrandingAndFeatureFlags(userData.entity?.internal_id, () => {
      if (redirectTo === ROUTES.ROLE_SELECT) {
        startTransition(() => void navigate({ to: ROUTES.ROLE_SELECT }));
        return;
      }
      // Only redirect to landing page if we are on the base /app path
      const currentPath = window.location.pathname;
      if (currentPath === "/app" || currentPath === "/") {
        setIsFeatureFlagLoading(false);
        const correctLanding = resolveLandingPage(userData);
        if (correctLanding) {
          setIsFeatureFlagLoading(false);
        }
        startTransition(() => void navigate({ to: correctLanding }));
      } else {
        // If we are already on a deep link and no role-select/redirect is needed,
        // hide the loader now as we're not navigating away.
        setIsFeatureFlagLoading(false);
      }
    });
  }, [
    getUserDetailData?.isFetching,
    getUserDetailData?.isLoading,
    getUserDetailData?.isSuccess,
    getUserDetailData?.data,
    navigate,
    setGlobalDateFormat,
    updateAuthStore,
    isRoleSelectRoute,
    loadBrandingAndFeatureFlags,
    isUpdatePasswordRoute,
    toast,
  ]);

  useEffect(() => {
    if (isUpdatePasswordRoute || isRoleSelectRoute) return;
    if (brandingFetchedRef.current || bootstrapErrorHandledRef.current) return;
    if (getUserDetailData?.isLoading || getUserDetailData?.isFetching) return;
    if (getUserDetailData?.isSuccess) return;

    bootstrapErrorHandledRef.current = true;
    setRoleResolved(true);
    setIsFeatureFlagLoading(false);

    const error = getUserDetailData?.error as
      | {
          message?: string;
          status?: number;
          errorResponse?: { message?: string };
        }
      | undefined;
    const errorMessage =
      error?.errorResponse?.message ??
      error?.message ??
      "Unable to load user session details.";

    toast({
      variant: "destructive",
      title: errorMessage,
    });

    if (error?.status === 403) {
      startTransition(() => void navigate({ to: "/forbidden" }));
    }
  }, [
    getUserDetailData?.error,
    getUserDetailData?.isFetching,
    getUserDetailData?.isLoading,
    getUserDetailData?.isSuccess,
    isRoleSelectRoute,
    isUpdatePasswordRoute,
    navigate,
    toast,
  ]);
  // Track whether the session has been verified via the user-detail API

  useEffect(() => {
    // Update document direction and local state when language changes
    const handleLanguageChange = (lng: string) => {
      const direction = i18n.dir(lng);
      document.documentElement.dir = direction;
      setDir(direction);
    };

    // Initial setup
    handleLanguageChange(i18n.language);

    // Subscribe to changes
    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [featureShowHideFlag, i18n]);

  /**
   * Call User Detail APi
   * @params uuid - user internal id in string
   * @returns Object- containing name , list of slug of features as comma separated.
   */

  // Detect mobile viewport
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false,
  );

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 768px)");
    setIsMobile(mql.matches);
  }, []);

  const [hasBootstrapped, setHasBootstrapped] = useState(false);

  useEffect(() => {
    if (!isFeatureFlagLoading && roleResolved && !hasBootstrapped) {
      setHasBootstrapped(true);
    }
  }, [isFeatureFlagLoading, roleResolved, hasBootstrapped]);

  // Hide app shell (sidebar, navbar) until role is resolved to avoid flashing dashboard for multi-role users
  const showAppShell = true;
  const showMobileOverlay = showAppShell && isMobile && !isSidebarToggle;

  // Show loader until role + feature flags + landing check are done (avoids flash of wrong page)
  const showLoader =
    !hasBootstrapped &&
    !isUpdatePasswordRoute &&
    !isRoleSelectRoute &&
    (roleResolutionPending || isFeatureFlagLoading);

  if (showLoader) {
    return <PageLoader />;
  }

  return (
    <main>
      <div className="h-screen">
        <div
          className="fixed top-0 left-0 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl"
          style={{ zIndex: -1 }}
        ></div>
        <div
          className="fixed bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
          style={{ zIndex: -1 }}
        ></div>
        <div
          className="fixed top-1/2 left-1/2 w-96 h-96 bg-pink-500/5 rounded-full blur-3xl"
          style={{ zIndex: -1 }}
        ></div>
        {<Sidebar />}
        {showMobileOverlay && (
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarToggle(true)}
            data-testid="sidebar-overlay"
          />
        )}
        <div
          className={`${dir !== "rtl" ? (!defferedIsSidebarToggle ? "pl-sidebarW-left-E" : "pl-sidebarW-left-S") : !defferedIsSidebarToggle ? "pl-sidebarW-right-E" : "pl-sidebarW-right-S"} h-full sm:gap-4 sm:pb-4`}
        >
          {showAppShell && <Navbar />}
          <div className="flex-1 items-start gap-2 p-4 relative">
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </div>
      {/* Global Data Visualization Modal - Controlled by Zustand Store */}
      <GlobalDataVisualizationModal />
    </main>
  );
};

export default AppLayout;
